export class AudioPlayer {
  private static audioContext: AudioContext | null = null;

  private static getAudioContext(): AudioContext {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      
      // Resume audio context if it's suspended (required by browsers for user interaction)
      if (this.audioContext.state === 'suspended') {
        this.audioContext.resume();
      }
    }
    return this.audioContext;
  }

  private static createEnhancedBeep(
    frequency: number,
    duration: number,
    volume: number,
    waveType: OscillatorType = 'sine'
  ): void {
    try {
      const context = this.getAudioContext();
      const oscillator = context.createOscillator();
      const gainNode = context.createGain();
      const filterNode = context.createBiquadFilter();

      // Create audio chain: oscillator -> filter -> gain -> destination
      oscillator.connect(filterNode);
      filterNode.connect(gainNode);
      gainNode.connect(context.destination);

      // Configure oscillator
      oscillator.frequency.value = frequency;
      oscillator.type = waveType;

      // Configure filter for better quality
      filterNode.type = 'lowpass';
      filterNode.frequency.value = frequency * 2;
      filterNode.Q.value = 1;

      // Configure volume envelope for smoother sound
      const now = context.currentTime;
      const attackTime = 0.01; // Quick attack
      const releaseTime = duration / 1000 * 0.3; // Gradual release

      gainNode.gain.setValueAtTime(0, now);
      gainNode.gain.linearRampToValueAtTime(volume, now + attackTime);
      gainNode.gain.setValueAtTime(volume, now + duration / 1000 - releaseTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, now + duration / 1000);

      oscillator.start(now);
      oscillator.stop(now + duration / 1000);
    } catch (error) {
      console.error('Failed to play enhanced beep sound:', error);
    }
  }

  static playBeep(
    frequency: number = 800,
    duration: number = 200,
    volume: number = 0.3
  ): void {
    this.createEnhancedBeep(frequency, duration, volume, 'sine');
  }

  static playViolationSound(): void {
    // Enhanced two-tone descending beep for violations (more noticeable)
    this.createEnhancedBeep(850, 600, 0.6, 'triangle'); // Higher volume, triangle wave for presence
    setTimeout(() => {
      this.createEnhancedBeep(550, 600, 0.5, 'triangle');
    }, 200);
  }

  static playWarningSound(): void {
    // Enhanced single beep for warnings (more prominent)
    this.createEnhancedBeep(1200, 300, 0.5, 'sawtooth'); // Sawtooth for attention
  }

  static playCriticalSound(): void {
    // Enhanced urgent triple beep for critical violations
    const playUrgentBeep = (delay: number) => {
      setTimeout(() => {
        this.createEnhancedBeep(1000, 240, 0.7, 'square'); // Square wave for urgency
      }, delay);
    };
    
    playUrgentBeep(0);
    playUrgentBeep(300);
    playUrgentBeep(600);
  }

  // New method to ensure audio context is ready
  static async ensureAudioReady(): Promise<void> {
    try {
      const context = this.getAudioContext();
      if (context.state === 'suspended') {
        await context.resume();
      }
    } catch (error) {
      console.error('Failed to initialize audio context:', error);
    }
  }
}